import { FormEvent, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiClientError } from '../lib/api';
import { Input, Select, Button } from '../atoms';
import { Modal } from './Modal';
import { useToast } from './Toast';

/**
 * JurisdictionCapModal — the relocated Exemption Mass-Update (PRD §8.3 + §2.5).
 *
 * The standalone mass-update page is retired; from a jurisdiction profile an
 * operator applies a FIXED CAP — a percentage or a flat minimum/abatement — that
 * mass-updates every parcel attached to that jurisdiction in one action.
 *
 *   POST /api/jurisdictions/:id/apply-cap { cap_percentage?, cap_minimum?, tax_year }
 *
 * The server upserts the jurisdiction-level cap config (jurisdiction_exemptions)
 * and opts every attached parcel into the appraised-value cap, returning the
 * count affected. (The one-off, single-parcel path is unchanged.)
 *
 * Part 2 §4 — a cap set here CASCADES: it rewrites every attached parcel in one
 * action. So the affected parcel count is shown BEFORE saving, not only in the
 * success toast afterwards, matching the confirmation pattern a stage-template
 * change already uses. GET /api/jurisdictions/:id/cap-impact is read-only and
 * runs only while the modal is open.
 */

export interface JurisdictionCapModalProps {
  open: boolean;
  onClose: () => void;
  jurisdictionId: string;
  jurisdictionLabel: string;
}

interface ApplyCapResult {
  parcels_attached: number;
  parcels_affected: number;
}

interface CapImpact {
  jurisdiction_id: string;
  jurisdiction_name: string | null;
  parcels_attached: number;
}

type CapKind = 'percentage' | 'minimum';

export function JurisdictionCapModal({ open, onClose, jurisdictionId, jurisdictionLabel }: JurisdictionCapModalProps) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [kind, setKind] = useState<CapKind>('percentage');
  const [value, setValue] = useState('');
  const [taxYear, setTaxYear] = useState<string>(() => String(new Date().getFullYear()));

  // The blast radius, fetched while the modal is open so the operator sees what
  // the cascade will reach before committing to it.
  const impactQuery = useQuery<CapImpact>({
    queryKey: ['jurisdiction-cap-impact', jurisdictionId],
    queryFn: ({ signal }) => api.get<CapImpact>(`/api/jurisdictions/${jurisdictionId}/cap-impact`, signal),
    enabled: open && !!jurisdictionId,
  });
  const attached = impactQuery.data?.parcels_attached;

  const mutation = useMutation<ApplyCapResult, Error, void>({
    mutationFn: () => {
      const body: Record<string, number> = { tax_year: Number(taxYear) };
      if (kind === 'percentage') body.cap_percentage = Number(value);
      else body.cap_minimum = Number(value);
      return api.post<ApplyCapResult>(`/api/jurisdictions/${jurisdictionId}/apply-cap`, body);
    },
    onSuccess: (result) => {
      toast({
        variant: 'success',
        message: `Cap applied to ${result.parcels_affected} of ${result.parcels_attached} attached parcel(s).`,
      });
      queryClient.invalidateQueries({ queryKey: ['jurisdictions', jurisdictionId] });
      queryClient.invalidateQueries({ queryKey: ['parcels-list'] });
      onClose();
    },
    onError: (err) => {
      const msg = err instanceof ApiClientError && err.body && typeof err.body === 'object' && 'error' in err.body
        ? String((err.body as { error: unknown }).error)
        : err.message;
      toast({ variant: 'error', message: msg });
    },
  });

  const canSubmit = value.trim() !== '' && Number(value) >= 0 && taxYear.trim() !== '';

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (canSubmit) mutation.mutate();
  }

  const labelStyle = {
    display: 'block',
    marginBottom: 'var(--sp-1)',
    fontSize: 'var(--fs-sm)',
    fontWeight: 'var(--fw-medium)',
    color: 'var(--color-ink)',
  } as const;

  return (
    <Modal
      open={open}
      onClose={() => { if (!mutation.isPending) onClose(); }}
      title={`Apply cap: ${jurisdictionLabel}`}
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={mutation.isPending}>
            Cancel
          </Button>
          <Button variant="primary" loading={mutation.isPending} disabled={!canSubmit} onClick={() => mutation.mutate()}>
            Apply to all parcels
          </Button>
        </>
      }
    >
      <form onSubmit={handleSubmit}>
        <p style={{ marginTop: 0, marginBottom: 'var(--sp-3)', fontSize: 'var(--fs-sm)', color: 'var(--color-ink-3)' }}>
          Applies the cap to every parcel attached to this jurisdiction. A one-off
          cap can still be set on an individual parcel.
        </p>
        {/* §4 — the count BEFORE the write. A jurisdiction with nothing attached
            still reads explicitly ("no parcels") rather than rendering nothing,
            so a zero is never mistaken for a figure that has not loaded. */}
        <p
          data-testid="cap-impact"
          style={{
            marginTop: 0, marginBottom: 'var(--sp-4)', padding: 'var(--sp-2) var(--sp-3)',
            background: 'var(--color-bg)', border: '1px solid var(--color-border)',
            borderRadius: 'var(--r-md)', fontSize: 'var(--fs-sm)', color: 'var(--color-ink-2)',
          }}
        >
          {impactQuery.isLoading
            ? 'Checking how many parcels this affects…'
            : attached == null
              ? 'Could not read how many parcels this affects.'
              : attached === 0
                ? 'No parcels are attached to this jurisdiction — this sets the cap for future parcels only.'
                : <>This will update <strong>{attached.toLocaleString()}</strong> attached parcel{attached === 1 ? '' : 's'}.</>}
        </p>
        <label style={{ display: 'block', marginBottom: 'var(--sp-3)' }}>
          <span style={labelStyle}>Cap type</span>
          <Select
            value={kind}
            onChange={e => setKind(e.target.value as CapKind)}
            options={[
              { value: 'percentage', label: 'Percentage (%)' },
              { value: 'minimum', label: 'Flat minimum ($)' },
            ]}
          />
        </label>
        <label style={{ display: 'block', marginBottom: 'var(--sp-3)' }}>
          <span style={labelStyle}>{kind === 'percentage' ? 'Cap percentage' : 'Cap minimum'}</span>
          <Input
            type="number"
            min={0}
            step={kind === 'percentage' ? '0.1' : '1'}
            value={value}
            onChange={e => setValue(e.target.value)}
            placeholder={kind === 'percentage' ? 'e.g. 10' : 'e.g. 25000'}
            required
          />
        </label>
        <label style={{ display: 'block' }}>
          <span style={labelStyle}>Tax year</span>
          <Input
            type="number"
            value={taxYear}
            onChange={e => setTaxYear(e.target.value)}
            required
          />
        </label>
      </form>
    </Modal>
  );
}
